import React from 'react';
import { Autocomplete, Chip, CircularProgress, TextField } from '@mui/material';
import { useGlobalization } from '@Translations';
import type { DefectCode } from '@Types/utils.types';

interface DefectAutoCompleteProps {
	defectCodes: string[];
	defectCodeOptions: DefectCode[];
	defectCodesLoading?: boolean;
	onDefectCodesChange: (codes: string[]) => void;
}

export const DefectAutoComplete = ({
	defectCodes,
	defectCodeOptions,
	defectCodesLoading,
	onDefectCodesChange,
}: DefectAutoCompleteProps) => {
	const { t } = useGlobalization();

	const selectedDefectCodeObjects = defectCodeOptions.filter(d =>
		defectCodes.includes(d.code),
	);

	return (
		<Autocomplete<DefectCode, true>
			multiple
			id="defect-codes"
			loading={defectCodesLoading}
			options={defectCodeOptions}
			value={selectedDefectCodeObjects}
			getOptionLabel={option => `${option.code} – ${option.desc}`}
			isOptionEqualToValue={(option, value) => option.code === value.code}
			onChange={(_event, newValue) =>
				onDefectCodesChange(newValue.map(d => d.code))
			}
			renderTags={(value, getTagProps) =>
				value.map((option, index) => (
					<Chip
						{...getTagProps({ index })}
						key={option.code}
						label={option.code}
						size="small"
					/>
				))
			}
			renderInput={params => (
				<TextField
					{...params}
					variant="outlined"
					label={t('Select Defects to include')}
					data-cy="defect-codes-input"
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{defectCodesLoading ? (
									<CircularProgress
										color="inherit"
										size={20}
									/>
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
};
